"use client";

import { useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { fetchWithAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";

const PrometricCoverageMap = dynamic(
  () => import("@/components/candidate-hub/PrometricCoverageMap"),
  { ssr: false }
);
const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

import {
  Users,
  Search,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Download,
  Copy,
  Check,
  X,
  CreditCard,
  GraduationCap,
  Mail,
  Phone,
  MessageSquare,
  UserCheck,
  BarChart3,
  Table,
  List
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DualScrollWrapper } from "@/components/directory/DualScrollWrapper";
import { SortToggle } from "@/components/directory/SortToggle";
import { PremiumMultiSelect } from "@/components/directory/PremiumMultiSelect";

interface Props {
  userEmail: string;
}

interface SummaryData {
  total_records: number;
  registered: number;
  deferred: number;
  cancelled: number;
  paid: number;
  no_cost: number;
  total_revenue_usd: number;
  charterholders: number;
  new_last_30_days: number;
  deferral_rate_pct: number;
  cancellation_rate_pct: number;
  freshly_paid?: number;
}

interface TestedSummary {
  total_sat: number;
  sit_rate_pct: number;
  physical_test_takers: number;
  virtual_test_takers: number;
  physical_pct: number;
  virtual_pct: number;
}

interface ScheduledSummary {
  total_scheduled: number;
  level_1: number;
  level_2: number;
  level_3: number;
  level_1_rate: number;
  level_2_rate: number;
  level_3_rate: number;
  scheduled_rate_pct: number;
  total_remote_scheduled?: number;
  remote_level_1?: number;
  remote_level_2?: number;
  remote_level_3?: number;
  remote_pct_of_scheduled?: number;
  total_cancelled?: number;
  cancelled_level_1?: number;
  cancelled_level_2?: number;
  cancelled_level_3?: number;
}

interface TestCenterItem {
  site_name: string;
  city: string;
  country: string;
  center_code: string;
  count: number;
  pct_of_physical: number;
}

interface TestCityItem {
  city: string;
  country: string;
  count: number;
  pct_of_physical: number;
}

interface BreakdownItem {
  label: string;
  value: number;
  pct: number;
}

interface CoverageData {
  test_centers_total: number;
  candidates_direct_access: number;
  candidates_gap_100km: number;
  candidates_severe_gap_200km: number;
  pct_direct_access: number;
  pct_gap: number;
  pct_severe: number;
}

interface CountryRow {
  country: string;
  macro_region: string;
  candidates: number;
  level1: number;
  level2: number;
  level3: number;
  prometric_centers: number;
  workstations: number;
  ratio: number | null;
  avg_dist_km: number;
  gap_candidates_100km: number;
  severe_gap_200km: number;
  gap_pct: number;
  coverage: string;
  coverage_color: string;
}

interface RegionSchedulingItem {
  region: string;
  registered: number;
  scheduled: number;
  remote: number;
  cancelled: number;
  scheduled_rate: number;
  remote_rate: number;
  cancelled_rate: number;
}

interface OversightPayload {
  cycle: string;
  last_updated: string;
  summary: SummaryData;
  tested_summary?: TestedSummary;
  scheduled_summary?: ScheduledSummary;
  level_breakdown: BreakdownItem[];
  geo_breakdown: BreakdownItem[];
  region_breakdown: BreakdownItem[];
  community_breakdown?: BreakdownItem[];
  region_scheduling?: RegionSchedulingItem[];
  top_test_centers?: TestCenterItem[];
  top_test_cities?: TestCityItem[];
  coverage: CoverageData;
  country_table: CountryRow[];
}

export function CycleOversightPanel({ userEmail }: Props) {
  const [selectedCycle, setSelectedCycle] = useState<"June 2026" | "December 2026">("June 2026");
  const [activeSubTab, setActiveSubTab] = useState<"metrics" | "coverage" | "candidates">("metrics");
  const [data, setData] = useState<OversightPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const getRegisteredForLevel = (lvlLabel: string) => {
    if (!data || !data.level_breakdown) return 0;
    const match = data.level_breakdown.find(x => x.label.toLowerCase() === lvlLabel.toLowerCase());
    return match ? match.value : 0;
  };

  const scheduledLevels = data ? [
    {
      label: "Level 1",
      value: data.scheduled_summary?.level_1 || 0,
      registered: getRegisteredForLevel("Level 1"),
      rate: data.scheduled_summary?.level_1_rate || 0
    },
    {
      label: "Level 2",
      value: data.scheduled_summary?.level_2 || 0,
      registered: getRegisteredForLevel("Level 2"),
      rate: data.scheduled_summary?.level_2_rate || 0
    },
    {
      label: "Level 3",
      value: data.scheduled_summary?.level_3 || 0,
      registered: getRegisteredForLevel("Level 3"),
      rate: data.scheduled_summary?.level_3_rate || 0
    }
  ] : [];

  const totalRegistered = data?.summary.registered || 0;
  const l1Reg = getRegisteredForLevel("Level 1");
  const l2Reg = getRegisteredForLevel("Level 2");
  const l3Reg = getRegisteredForLevel("Level 3");

  const l1Sched = data?.scheduled_summary?.level_1 || 0;
  const l2Sched = data?.scheduled_summary?.level_2 || 0;
  const l3Sched = data?.scheduled_summary?.level_3 || 0;
  const totalSched = data?.scheduled_summary?.total_scheduled || 0;

  const l1Remote = data?.scheduled_summary?.remote_level_1 || 0;
  const l2Remote = data?.scheduled_summary?.remote_level_2 || 0;
  const l3Remote = data?.scheduled_summary?.remote_level_3 || 0;
  const totalRemote = data?.scheduled_summary?.total_remote_scheduled || 0;

  const l1Cancel = data?.scheduled_summary?.cancelled_level_1 || 0;
  const l2Cancel = data?.scheduled_summary?.cancelled_level_2 || 0;
  const l3Cancel = data?.scheduled_summary?.cancelled_level_3 || 0;
  const totalCancel = data?.scheduled_summary?.total_cancelled || 0;

  // View Mode States for Cycle Metrics cards
  const [geoViewMode, setGeoViewMode] = useState<"list" | "chart">("chart");
  const [geoScaleType, setGeoScaleType] = useState<"linear" | "log">("log");
  const [showAllGeo, setShowAllGeo] = useState<boolean>(true);
  const [overviewViewMode, setOverviewViewMode] = useState<"table" | "chart">("table");

  const formattedSyncTime = useMemo(() => {
    if (!data?.last_updated) return "";
    if (data.last_updated.includes("Snapshot") || data.last_updated.includes("Live CRM")) {
      return data.last_updated;
    }
    try {
      const date = new Date(data.last_updated);
      if (isNaN(date.getTime())) {
        return data.last_updated;
      }
      
      const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
      if (seconds < 0) return "just now";

      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
          return `${days} day${days > 1 ? "s" : ""} ${remainingHours} hour${remainingHours > 1 ? "s" : ""} ago`;
        }
        return `${days} day${days > 1 ? "s" : ""} ago`;
      }

      if (hours > 0) {
        const remainingMinutes = minutes % 60;
        if (remainingMinutes > 0) {
          return `${hours} hour${hours > 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""} ago`;
        }
        return `${hours} hour${hours > 1 ? "s" : ""} ago`;
      }

      if (minutes > 0) {
        return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
      }

      return "just now";
    } catch (e) {
      return data.last_updated;
    }
  }, [data?.last_updated]);

  // Chart Memos using official CMT Association Brand Standards Colors
  // Deep Teal: #004d57, Mid Teal: #4b8892, Sage: #a9c4bb, Crimson: #ae232d, Orange: #ef4621, Plum: #5e4d66
  const geoCountriesChartOption = useMemo(() => {
    if (!data?.geo_breakdown) return {};
    const items = showAllGeo ? data.geo_breakdown : data.geo_breakdown.slice(0, 10);
    const chartItems = [...items].reverse();
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any[]) => {
          const item = params[0];
          const raw = data.geo_breakdown.find((g) => g.label === item.name);
          return `<div class="font-bold text-xs text-[#004d57]">${item.name}</div><div class="text-xs text-[#004d57] font-semibold">${raw?.value ?? item.value} candidates (${raw?.pct || 0}%)</div>`;
        }
      },
      grid: { left: "3%", right: "14%", bottom: "3%", top: "3%", containLabel: true },
      xAxis: {
        type: geoScaleType === "log" ? "log" : "value",
        logBase: 10,
        min: geoScaleType === "log" ? 1 : undefined,
        axisLabel: {
          fontSize: 10,
          color: "#595959",
          formatter: (val: number) => {
            if (val >= 1000) return (val / 1000).toFixed(0) + "k";
            return val.toString();
          }
        },
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } }
      },
      yAxis: {
        type: "category",
        data: chartItems.map((i) => i.label),
        axisLabel: { fontSize: 11, color: "#2c2c2c", fontWeight: "600" }
      },
      series: [
        {
          name: "Candidates",
          type: "bar",
          data: chartItems.map((i) => (geoScaleType === "log" ? Math.max(1, i.value) : i.value)),
          itemStyle: {
            color: "#004d57",
            borderRadius: [0, 4, 4, 0]
          },
          label: {
            show: true,
            position: "right",
            fontSize: 10,
            fontWeight: "bold",
            color: "#004d57",
            formatter: (params: any) => {
              const item = chartItems[params.dataIndex];
              return item ? `${item.value}` : params.value;
            }
          }
        }
      ]
    };
  }, [data?.geo_breakdown, geoScaleType, showAllGeo]);

  const geoCommunitiesChartOption = useMemo(() => {
    if (!data?.community_breakdown) return {};
    const items = showAllGeo ? data.community_breakdown : data.community_breakdown.slice(0, 10);
    const chartItems = [...items].reverse();
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any[]) => {
          const item = params[0];
          const raw = data.community_breakdown?.find((c) => c.label === item.name);
          return `<div class="font-bold text-xs text-[#004d57]">${item.name}</div><div class="text-xs text-[#4b8892] font-semibold">${raw?.value ?? item.value} candidates (${raw?.pct || 0}%)</div>`;
        }
      },
      grid: { left: "3%", right: "14%", bottom: "3%", top: "3%", containLabel: true },
      xAxis: {
        type: geoScaleType === "log" ? "log" : "value",
        logBase: 10,
        min: geoScaleType === "log" ? 1 : undefined,
        axisLabel: {
          fontSize: 10,
          color: "#595959",
          formatter: (val: number) => {
            if (val >= 1000) return (val / 1000).toFixed(0) + "k";
            return val.toString();
          }
        },
        splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } }
      },
      yAxis: {
        type: "category",
        data: chartItems.map((i) => i.label),
        axisLabel: { fontSize: 11, color: "#2c2c2c", fontWeight: "600" }
      },
      series: [
        {
          name: "Candidates",
          type: "bar",
          data: chartItems.map((i) => (geoScaleType === "log" ? Math.max(1, i.value) : i.value)),
          itemStyle: {
            color: "#4b8892",
            borderRadius: [0, 4, 4, 0]
          },
          label: {
            show: true,
            position: "right",
            fontSize: 10,
            fontWeight: "bold",
            color: "#4b8892",
            formatter: (params: any) => {
              const item = chartItems[params.dataIndex];
              return item ? `${item.value}` : params.value;
            }
          }
        }
      ]
    };
  }, [data?.community_breakdown, geoScaleType, showAllGeo]);

  const overviewLevelChartOption = useMemo(() => {
    return {
      title: { text: "By Exam Level", left: "center", textStyle: { fontSize: 12, fontWeight: "bold", color: "#004d57" } },
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { data: ["Registered", "Scheduled", "Cancelled"], bottom: 0, textStyle: { fontSize: 10, color: "#595959" } },
      grid: { left: "3%", right: "4%", bottom: "18%", top: "18%", containLabel: true },
      xAxis: {
        type: "category",
        data: ["CMT Level 1", "CMT Level 2", "CMT Level 3"],
        axisLabel: { fontSize: 11, fontWeight: "bold", color: "#004d57" }
      },
      yAxis: { type: "value", axisLabel: { fontSize: 10, color: "#595959" }, splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } } },
      series: [
        { name: "Registered", type: "bar", data: [l1Reg, l2Reg, l3Reg], itemStyle: { color: "#004d57", borderRadius: [4, 4, 0, 0] } },
        { name: "Scheduled", type: "bar", data: [l1Sched, l2Sched, l3Sched], itemStyle: { color: "#4b8892", borderRadius: [4, 4, 0, 0] } },
        { name: "Cancelled", type: "bar", data: [l1Cancel, l2Cancel, l3Cancel], itemStyle: { color: "#ae232d", borderRadius: [4, 4, 0, 0] } }
      ]
    };
  }, [l1Reg, l2Reg, l3Reg, l1Sched, l2Sched, l3Sched, l1Cancel, l2Cancel, l3Cancel]);

  const overviewRegionChartOption = useMemo(() => {
    if (!data?.region_scheduling) return {};
    const regions = data.region_scheduling;
    const categories = regions.map((r) => {
      if (r.region === "AMER") return "AMER";
      if (r.region === "APAC") return "APAC";
      if (r.region === "EMEA") return "EMEA";
      if (r.region === "LATAM") return "LATAM";
      return r.region;
    });

    return {
      title: { text: "By Geographic Region", left: "center", textStyle: { fontSize: 12, fontWeight: "bold", color: "#004d57" } },
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { data: ["Registered", "Scheduled", "Cancelled"], bottom: 0, textStyle: { fontSize: 10, color: "#595959" } },
      grid: { left: "3%", right: "4%", bottom: "18%", top: "18%", containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: { fontSize: 11, fontWeight: "bold", color: "#004d57" }
      },
      yAxis: { type: "value", axisLabel: { fontSize: 10, color: "#595959" }, splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } } },
      series: [
        { name: "Registered", type: "bar", data: regions.map((r) => r.registered), itemStyle: { color: "#004d57", borderRadius: [4, 4, 0, 0] } },
        { name: "Scheduled", type: "bar", data: regions.map((r) => r.scheduled), itemStyle: { color: "#4b8892", borderRadius: [4, 4, 0, 0] } },
        { name: "Cancelled", type: "bar", data: regions.map((r) => r.cancelled || 0), itemStyle: { color: "#ae232d", borderRadius: [4, 4, 0, 0] } }
      ]
    };
  }, [data?.region_scheduling]);

  const schedulingDeliveryLevelChartOption = useMemo(() => {
    const l1InCenter = Math.max(0, l1Sched - l1Remote);
    const l2InCenter = Math.max(0, l2Sched - l2Remote);
    const l3InCenter = Math.max(0, l3Sched - l3Remote);

    return {
      title: { text: "Delivery Channel (By Exam Level)", left: "center", textStyle: { fontSize: 12, fontWeight: "bold", color: "#004d57" } },
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { data: ["Total Scheduled", "In-Center (Physical)", "Remote / Virtual"], bottom: 0, textStyle: { fontSize: 10, color: "#595959" } },
      grid: { left: "3%", right: "4%", bottom: "18%", top: "18%", containLabel: true },
      xAxis: {
        type: "category",
        data: ["CMT Level 1", "CMT Level 2", "CMT Level 3"],
        axisLabel: { fontSize: 11, fontWeight: "bold", color: "#004d57" }
      },
      yAxis: { type: "value", axisLabel: { fontSize: 10, color: "#595959" }, splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } } },
      series: [
        { name: "Total Scheduled", type: "bar", data: [l1Sched, l2Sched, l3Sched], itemStyle: { color: "#004d57", borderRadius: [4, 4, 0, 0] } },
        { name: "In-Center (Physical)", type: "bar", data: [l1InCenter, l2InCenter, l3InCenter], itemStyle: { color: "#4b8892", borderRadius: [4, 4, 0, 0] } },
        { name: "Remote / Virtual", type: "bar", data: [l1Remote, l2Remote, l3Remote], itemStyle: { color: "#ef4621", borderRadius: [4, 4, 0, 0] } }
      ]
    };
  }, [l1Sched, l2Sched, l3Sched, l1Remote, l2Remote, l3Remote]);

  const schedulingDeliveryRegionChartOption = useMemo(() => {
    if (!data?.region_scheduling) return {};
    const regions = data.region_scheduling;
    const categories = regions.map((r) => {
      if (r.region === "AMER") return "AMER";
      if (r.region === "APAC") return "APAC";
      if (r.region === "EMEA") return "EMEA";
      if (r.region === "LATAM") return "LATAM";
      return r.region;
    });

    return {
      title: { text: "Delivery Channel (By Region)", left: "center", textStyle: { fontSize: 12, fontWeight: "bold", color: "#004d57" } },
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { data: ["Total Scheduled", "In-Center (Physical)", "Remote / Virtual"], bottom: 0, textStyle: { fontSize: 10, color: "#595959" } },
      grid: { left: "3%", right: "4%", bottom: "18%", top: "18%", containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: { fontSize: 11, fontWeight: "bold", color: "#004d57" }
      },
      yAxis: { type: "value", axisLabel: { fontSize: 10, color: "#595959" }, splitLine: { lineStyle: { type: "dashed", color: "#f0f0f0" } } },
      series: [
        { name: "Total Scheduled", type: "bar", data: regions.map((r) => r.scheduled), itemStyle: { color: "#004d57", borderRadius: [4, 4, 0, 0] } },
        { name: "In-Center (Physical)", type: "bar", data: regions.map((r) => Math.max(0, r.scheduled - r.remote)), itemStyle: { color: "#4b8892", borderRadius: [4, 4, 0, 0] } },
      ]
    };
  }, [data?.region_scheduling]);

  // Test Centers & Cities Search & View All States
  const [centerSearch, setCenterSearch] = useState<string>("");
  const [citySearch, setCitySearch] = useState<string>("");
  const [showAllCenters, setShowAllCenters] = useState<boolean>(false);
  const [showAllCities, setShowAllCities] = useState<boolean>(false);

  const filteredCenters = useMemo(() => {
    if (!data?.top_test_centers) return [];
    if (!centerSearch.trim()) {
      return showAllCenters ? data.top_test_centers : data.top_test_centers.slice(0, 10);
    }
    const q = centerSearch.toLowerCase();
    return data.top_test_centers.filter(
      (c) =>
        c.site_name.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.country.toLowerCase().includes(q) ||
        (c.center_code && c.center_code.toLowerCase().includes(q))
    );
  }, [data?.top_test_centers, centerSearch, showAllCenters]);

  const filteredCities = useMemo(() => {
    if (!data?.top_test_cities) return [];
    if (!citySearch.trim()) {
      return showAllCities ? data.top_test_cities : data.top_test_cities.slice(0, 10);
    }
    const q = citySearch.toLowerCase();
    return data.top_test_cities.filter(
      (c) => c.city.toLowerCase().includes(q) || c.country.toLowerCase().includes(q)
    );
  }, [data?.top_test_cities, citySearch, showAllCities]);

  // Candidate Database table state (Staff Directory style with in-header PremiumMultiSelects)
  const [candidatesList, setCandidatesList] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState<boolean>(false);
  
  // Global Keyword Search State
  const [candidateSearch, setCandidateSearch] = useState<string>("");

  // Column Header Filter States (PremiumMultiSelects & Column Inputs)
  const [filterLevel, setFilterLevel] = useState<string[]>([]);
  const [excludeLevel, setExcludeLevel] = useState<boolean>(false);

  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [excludeStatus, setExcludeStatus] = useState<boolean>(false);

  const [filterAttendance, setFilterAttendance] = useState<string[]>([]);
  const [excludeAttendance, setExcludeAttendance] = useState<boolean>(false);

  const [filterPrometricCenter, setFilterPrometricCenter] = useState<string[]>([]);
  const [excludePrometricCenter, setExcludePrometricCenter] = useState<boolean>(false);

  const [filterPayment, setFilterPayment] = useState<string[]>([]);
  const [excludePayment, setExcludePayment] = useState<boolean>(false);

  const [filterMembershipJourney, setFilterMembershipJourney] = useState<string[]>([]);
  const [excludeMembershipJourney, setExcludeMembershipJourney] = useState<boolean>(false);

  const [filterCommunity, setFilterCommunity] = useState<string[]>([]);
  const [excludeCommunity, setExcludeCommunity] = useState<boolean>(false);

  const [filterChapter, setFilterChapter] = useState<string[]>([]);
  const [excludeChapter, setExcludeChapter] = useState<boolean>(false);

  const [filterCity, setFilterCity] = useState<string[]>([]);
  const [excludeCity, setExcludeCity] = useState<boolean>(false);

  const [filterState, setFilterState] = useState<string[]>([]);
  const [excludeState, setExcludeState] = useState<boolean>(false);

  const [filterCountry, setFilterCountry] = useState<string[]>([]);
  const [excludeCountry, setExcludeCountry] = useState<boolean>(false);

  const [filterRegion, setFilterRegion] = useState<string[]>([]);
  const [excludeRegion, setExcludeRegion] = useState<boolean>(false);

  const [filterDistMin, setFilterDistMin] = useState<string>("");
  const [filterDistMax, setFilterDistMax] = useState<string>("");
  const [filterCenterMatch, setFilterCenterMatch] = useState<string[]>([]);
  const [excludeCenterMatch, setExcludeCenterMatch] = useState<boolean>(false);

  const [candidateIdSearch, setCandidateIdSearch] = useState<string>("");
  const [candidateNameSearch, setCandidateNameSearch] = useState<string>("");
  const [filterEmail, setFilterEmail] = useState<string>("");
  const [filterPhone, setFilterPhone] = useState<string>("");
  const [filterDiscord, setFilterDiscord] = useState<string>("");

  // Pagination & Sorting State
  const [candidatePage, setCandidatePage] = useState<number>(1);
  const [candidatePageSize, setCandidatePageSize] = useState<number>(50);
  const [candidateSortConfig, setCandidateSortConfig] = useState<{ col: string | null; order: "asc" | "desc" }>({
    col: null,
    order: "asc",
  });
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [exportingCSV, setExportingCSV] = useState<boolean>(false);

  // Country filter for coverage sub-tab
  const [countrySearch, setCountrySearch] = useState<string>("");
  const [coverageRegionFilter, setCoverageRegionFilter] = useState<string>("all");

  useEffect(() => {
    fetchOversightData(selectedCycle);
  }, [selectedCycle]);

  useEffect(() => {
    if (activeSubTab === "candidates") {
      fetchCandidates(selectedCycle);
    }
  }, [activeSubTab, selectedCycle]);

  async function fetchOversightData(cycle: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/v1/candidate-hub/oversight?cycle=${encodeURIComponent(cycle)}`);
      if (!res.ok) {
        throw new Error(`Failed to load oversight data (${res.status})`);
      }
      const payload = await res.json();
      setData(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }

  async function fetchCandidates(cycle: string) {
    setCandidatesLoading(true);
    try {
      const res = await fetchWithAuth(`/api/v1/candidate-hub/candidates?cycle=${encodeURIComponent(cycle)}&page_size=2000`);
      if (res.ok) {
        const payload = await res.json();
        setCandidatesList(payload.candidates || []);
      }
    } catch (err) {
      console.error("Failed to fetch candidates list", err);
    } finally {
      setCandidatesLoading(false);
    }
  }

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter country table for Coverage sub-tab
  const filteredCountries = (data?.country_table || []).filter((row) => {
    const matchesSearch = row.country.toLowerCase().includes(countrySearch.toLowerCase());
    const matchesRegion = coverageRegionFilter === "all" || row.macro_region === coverageRegionFilter;
    return matchesSearch && matchesRegion;
  });

  // Extract unique location options dynamically for PremiumMultiSelects
  const availableCommunities = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.community && c.community.trim()) set.add(c.community.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availableChapters = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.chapter && c.chapter.trim()) set.add(c.chapter.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availableCities = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.city && c.city.trim()) set.add(c.city.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availableStates = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.state && c.state.trim()) set.add(c.state.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.country && c.country.trim()) set.add(c.country.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availableMembershipJourneys = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.membership_journey && c.membership_journey.trim()) set.add(c.membership_journey.trim());
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  const availablePrometricCenters = useMemo(() => {
    const set = new Set<string>();
    candidatesList.forEach((c) => {
      if (c.is_virtual) {
        set.add("Virtual / Remote Proctored");
      } else if (c.actual_site_name && c.actual_site_name.trim()) {
        set.add(c.actual_site_name.trim());
      }
    });
    return Array.from(set).sort();
  }, [candidatesList]);

  // Candidate Database Filtering & Sorting Logic
  const processedCandidates = useMemo(() => {
    let result = [...candidatesList];

    // Global Keyword Search
    if (candidateSearch.trim()) {
      const q = candidateSearch.toLowerCase().trim();
      result = result.filter((c) => {
        const fullName = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
        const idStr = String(c.member_id || "");
        const emailStr = (c.email || "").toLowerCase();
        const phoneStr = (c.phone || "").toLowerCase();
        const discordStr = (c.discord_id || "").toLowerCase();
        const memJourneyStr = (c.membership_journey || "").toLowerCase();
        const countryStr = (c.country || "").toLowerCase();
        const cityStr = (c.city || "").toLowerCase();
        const stateStr = (c.state || "").toLowerCase();
        const chapterStr = (c.chapter || "").toLowerCase();
        const communityStr = (c.community || "").toLowerCase();
        const regionStr = (c.region || "").toLowerCase();
        const levelStr = (c.level || "").toLowerCase();
        const statusStr = (c.status || "").toLowerCase();
        const paymentStr = (c.payment_status || "").toLowerCase();
        const journeyStr = (c.program_journey || "").toLowerCase();
        const siteStr = (c.is_virtual ? "virtual / remote proctored" : c.actual_site_name || "").toLowerCase();
        const attStr = c.has_sat_exam ? "sat exam" : "pending / scheduled";
        const closeSiteStr = (c.closest_center_name || "").toLowerCase();
        const closeDistStr = c.closest_center_dist_miles !== null && c.closest_center_dist_miles !== undefined ? `${c.closest_center_dist_miles} mi` : "";
        const matchStr = (c.exam_center_match || "").toLowerCase();
        return (
          fullName.includes(q) ||
          idStr.includes(q) ||
          emailStr.includes(q) ||
          phoneStr.includes(q) ||
          discordStr.includes(q) ||
          memJourneyStr.includes(q) ||
          countryStr.includes(q) ||
          cityStr.includes(q) ||
          stateStr.includes(q) ||
          chapterStr.includes(q) ||
          communityStr.includes(q) ||
          regionStr.includes(q) ||
          levelStr.includes(q) ||
          statusStr.includes(q) ||
          paymentStr.includes(q) ||
          journeyStr.includes(q) ||
          siteStr.includes(q) ||
          attStr.includes(q) ||
          closeSiteStr.includes(q) ||
          closeDistStr.includes(q) ||
          matchStr.includes(q)
        );
      });
    }

    // Column Filter: Level
    if (filterLevel.length > 0) {
      result = result.filter((c) => {
        const match = filterLevel.some((lvl) => (c.level || "").toLowerCase().includes(lvl.toLowerCase()));
        return excludeLevel ? !match : match;
      });
    }

    // Column Filter: Status
    if (filterStatus.length > 0) {
      result = result.filter((c) => {
        const match = filterStatus.some((st) => (c.status || "").toLowerCase() === st.toLowerCase());
        return excludeStatus ? !match : match;
      });
    }

    // Column Filter: Attendance
    if (filterAttendance.length > 0) {
      result = result.filter((c) => {
        const attLabel = c.has_sat_exam ? "Sat Exam" : "Pending / Scheduled";
        const match = filterAttendance.some((a) => attLabel.toLowerCase() === a.toLowerCase());
        return excludeAttendance ? !match : match;
      });
    }

    // Column Filter: Prometric Center
    if (filterPrometricCenter.length > 0) {
      result = result.filter((c) => {
        const siteLabel = c.is_virtual ? "Virtual / Remote Proctored" : (c.actual_site_name || "");
        const match = filterPrometricCenter.some((pc) => siteLabel.toLowerCase() === pc.toLowerCase());
        return excludePrometricCenter ? !match : match;
      });
    }

    // Column Filter: Distance Range (Miles)
    if (filterDistMin.trim() !== "" || filterDistMax.trim() !== "") {
      const minD = filterDistMin.trim() !== "" ? parseFloat(filterDistMin) : -Infinity;
      const maxD = filterDistMax.trim() !== "" ? parseFloat(filterDistMax) : Infinity;
      result = result.filter((c) => {
        const d = c.closest_center_dist_miles;
        if (d === null || d === undefined || isNaN(d)) return false;
        return d >= minD && d <= maxD;
      });
    }

    // Column Filter: Exam Location Match
    if (filterCenterMatch.length > 0) {
      result = result.filter((c) => {
        let matchLabel = "Registered / Pending";
        if (c.exam_center_match === "closest_center") matchLabel = "Closest Center";
        else if (c.exam_center_match === "other_center") matchLabel = "Other Center";
        else if (c.exam_center_match === "virtual") matchLabel = "Virtual / Remote";

        const match = filterCenterMatch.some((m) => matchLabel.toLowerCase() === m.toLowerCase());
        return excludeCenterMatch ? !match : match;
      });
    }

    // Column Filter: Payment Status
    if (filterPayment.length > 0) {
      result = result.filter((c) => {
        const match = filterPayment.some((pm) => (c.payment_status || "").toLowerCase() === pm.toLowerCase());
        return excludePayment ? !match : match;
      });
    }

    // Column Filter: Membership Journey
    if (filterMembershipJourney.length > 0) {
      result = result.filter((c) => {
        const match = filterMembershipJourney.some((mj) => (c.membership_journey || "").toLowerCase() === mj.toLowerCase());
        return excludeMembershipJourney ? !match : match;
      });
    }

    // Column Filter: Community (cluster_center_name)
    if (filterCommunity.length > 0) {
      result = result.filter((c) => {
        const match = filterCommunity.some((cm) => (c.community || "").toLowerCase() === cm.toLowerCase());
        return excludeCommunity ? !match : match;
      });
    }

    // Column Filter: Chapter
    if (filterChapter.length > 0) {
      result = result.filter((c) => {
        const match = filterChapter.some((ch) => (c.chapter || "").toLowerCase() === ch.toLowerCase());
        return excludeChapter ? !match : match;
      });
    }

    // Column Filter: City / Locality (city_name)
    if (filterCity.length > 0) {
      result = result.filter((c) => {
        const match = filterCity.some((ct) => (c.city || "").toLowerCase() === ct.toLowerCase());
        return excludeCity ? !match : match;
      });
    }

    // Column Filter: State (city_admin1)
    if (filterState.length > 0) {
      result = result.filter((c) => {
        const match = filterState.some((st) => (c.state || "").toLowerCase() === st.toLowerCase());
        return excludeState ? !match : match;
      });
    }

    // Column Filter: Country (city_country)
    if (filterCountry.length > 0) {
      result = result.filter((c) => {
        const match = filterCountry.some((ct) => (c.country || "").toLowerCase() === ct.toLowerCase());
        return excludeCountry ? !match : match;
      });
    }

    // Column Filter: Region
    if (filterRegion.length > 0) {
      result = result.filter((c) => {
        const match = filterRegion.some((rg) => (c.region || "").toUpperCase() === rg.toUpperCase());
        return excludeRegion ? !match : match;
      });
    }

    // Column Header Text Search Inputs
    if (candidateIdSearch.trim()) {
      const q = candidateIdSearch.trim();
      result = result.filter((c) => String(c.member_id || "").includes(q));
    }
    if (candidateNameSearch.trim()) {
      const q = candidateNameSearch.toLowerCase().trim();
      result = result.filter((c) =>
        `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(q)
      );
    }
    if (filterEmail.trim()) {
      const q = filterEmail.toLowerCase().trim();
      result = result.filter((c) => (c.email || "").toLowerCase().includes(q));
    }
    if (filterPhone.trim()) {
      const q = filterPhone.toLowerCase().trim();
      result = result.filter((c) => (c.phone || "").toLowerCase().includes(q));
    }
    if (filterDiscord.trim()) {
      const q = filterDiscord.toLowerCase().trim();
      result = result.filter((c) => (c.discord_id || "").toLowerCase().includes(q));
    }

    // Column Sorting
    if (candidateSortConfig.col) {
      const col = candidateSortConfig.col;
      const order = candidateSortConfig.order === "asc" ? 1 : -1;
      result.sort((a, b) => {
        let valA: any = "";
        let valB: any = "";

        if (col === "member_id") {
          valA = a.member_id || 0;
          valB = b.member_id || 0;
        } else if (col === "name") {
          valA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          valB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
        } else if (col === "email") {
          valA = a.email || "";
          valB = b.email || "";
        } else if (col === "phone") {
          valA = a.phone || "";
          valB = b.phone || "";
        } else if (col === "discord_id") {
          valA = a.discord_id || "";
          valB = b.discord_id || "";
        } else if (col === "membership_journey") {
          valA = a.membership_journey || "";
          valB = b.membership_journey || "";
        } else if (col === "level") {
          valA = a.level || "";
          valB = b.level || "";
        } else if (col === "status") {
          valA = a.status || "";
          valB = b.status || "";
        } else if (col === "payment_status") {
          valA = a.payment_status || "";
          valB = b.payment_status || "";
        } else if (col === "community") {
          valA = a.community || "";
          valB = b.community || "";
        } else if (col === "chapter") {
          valA = a.chapter || "";
          valB = b.chapter || "";
        } else if (col === "city") {
          valA = a.city || "";
          valB = b.city || "";
        } else if (col === "state") {
          valA = a.state || "";
          valB = b.state || "";
        } else if (col === "country") {
          valA = a.country || "";
          valB = b.country || "";
        } else if (col === "region") {
          valA = a.region || "";
          valB = b.region || "";
        } else if (col === "has_sat_exam") {
          valA = a.has_sat_exam ? 1 : 0;
          valB = b.has_sat_exam ? 1 : 0;
        } else if (col === "actual_site_name") {
          valA = a.is_virtual ? "Virtual / Remote Proctored" : (a.actual_site_name || "");
          valB = b.is_virtual ? "Virtual / Remote Proctored" : (b.actual_site_name || "");
        } else if (col === "closest_center_dist_miles") {
          valA = a.closest_center_dist_miles !== null && a.closest_center_dist_miles !== undefined ? a.closest_center_dist_miles : 999999;
          valB = b.closest_center_dist_miles !== null && b.closest_center_dist_miles !== undefined ? b.closest_center_dist_miles : 999999;
        } else if (col === "exam_center_match") {
          valA = a.exam_center_match || "";
          valB = b.exam_center_match || "";
        }

        if (valA < valB) return -1 * order;
        if (valA > valB) return 1 * order;
        return 0;
      });
    }

    return result;
  }, [
    candidatesList,
    candidateSearch,
    filterLevel,
    excludeLevel,
    filterStatus,
    excludeStatus,
    filterAttendance,
    excludeAttendance,
    filterPrometricCenter,
    excludePrometricCenter,
    filterDistMin,
    filterDistMax,
    filterCenterMatch,
    excludeCenterMatch,
    filterPayment,
    excludePayment,
    filterMembershipJourney,
    excludeMembershipJourney,
    filterCommunity,
    excludeCommunity,
    filterChapter,
    excludeChapter,
    filterCity,
    excludeCity,
    filterState,
    excludeState,
    filterCountry,
    excludeCountry,
    filterRegion,
    excludeRegion,
    candidateIdSearch,
    candidateNameSearch,
    filterEmail,
    filterPhone,
    filterDiscord,
    candidateSortConfig,
  ]);

  const hasCandidateFilters =
    candidateSearch !== "" ||
    filterLevel.length > 0 ||
    filterStatus.length > 0 ||
    filterAttendance.length > 0 ||
    filterPrometricCenter.length > 0 ||
    filterDistMin !== "" ||
    filterDistMax !== "" ||
    filterCenterMatch.length > 0 ||
    filterPayment.length > 0 ||
    filterMembershipJourney.length > 0 ||
    filterCommunity.length > 0 ||
    filterChapter.length > 0 ||
    filterCity.length > 0 ||
    filterState.length > 0 ||
    filterCountry.length > 0 ||
    filterRegion.length > 0 ||
    candidateIdSearch !== "" ||
    candidateNameSearch !== "" ||
    filterEmail !== "" ||
    filterPhone !== "" ||
    filterDiscord !== "";

  const clearCandidateFilters = () => {
    setCandidateSearch("");
    setFilterLevel([]);
    setExcludeLevel(false);
    setFilterStatus([]);
    setExcludeStatus(false);
    setFilterAttendance([]);
    setExcludeAttendance(false);
    setFilterPrometricCenter([]);
    setExcludePrometricCenter(false);
    setFilterDistMin("");
    setFilterDistMax("");
    setFilterCenterMatch([]);
    setExcludeCenterMatch(false);
    setFilterPayment([]);
    setExcludePayment(false);
    setFilterMembershipJourney([]);
    setExcludeMembershipJourney(false);
    setFilterCommunity([]);
    setExcludeCommunity(false);
    setFilterChapter([]);
    setExcludeChapter(false);
    setFilterCity([]);
    setExcludeCity(false);
    setFilterState([]);
    setExcludeState(false);
    setFilterCountry([]);
    setExcludeCountry(false);
    setFilterRegion([]);
    setExcludeRegion(false);
    setCandidateIdSearch("");
    setCandidateNameSearch("");
    setFilterEmail("");
    setFilterPhone("");
    setFilterDiscord("");
    setCandidatePage(1);
  };

  const totalCandidateRecords = processedCandidates.length;
  const totalCandidatePages = Math.max(1, Math.ceil(totalCandidateRecords / candidatePageSize));

  const paginatedCandidates = useMemo(() => {
    const start = (candidatePage - 1) * candidatePageSize;
    return processedCandidates.slice(start, start + candidatePageSize);
  }, [processedCandidates, candidatePage, candidatePageSize]);

  const exportCandidatesCSV = () => {
    setExportingCSV(true);
    try {
      const headers = [
        "Member ID", "First Name", "Last Name", "Exam Level", "Registration Status",
        "Payment Status", "Payment Amount", "Community", "Chapter", "City", "State", "Country", "Region",
        "Program Journey", "Level 1 Status", "Level 2 Status", "Level 3 Status", "Payment Date",
        "Closest Center Name", "Closest Center Code", "Distance to Closest Center (Miles)", "Actual Center Distance (Miles)", "Exam Location Match"
      ];
      const rows = processedCandidates.map((c) => [
        c.member_id || "",
        `"${(c.first_name || "").replace(/"/g, '""')}"`,
        `"${(c.last_name || "").replace(/"/g, '""')}"`,
        `"${c.level || ""}"`,
        `"${c.status || ""}"`,
        `"${c.payment_status || ""}"`,
        `"${c.payment_amount || ""}"`,
        `"${(c.community || "").replace(/"/g, '""')}"`,
        `"${(c.chapter || "").replace(/"/g, '""')}"`,
        `"${(c.city || "").replace(/"/g, '""')}"`,
        `"${(c.state || "").replace(/"/g, '""')}"`,
        `"${(c.country || "").replace(/"/g, '""')}"`,
        `"${c.region || ""}"`,
        `"${c.program_journey || ""}"`,
        `"${c.level1_status || ""}"`,
        `"${c.level2_status || ""}"`,
        `"${c.level3_status || ""}"`,
        `"${c.last_updated || ""}"`,
        `"${(c.closest_center_name || "").replace(/"/g, '""')}"`,
        `"${c.closest_center_code || ""}"`,
        `"${c.closest_center_dist_miles !== null && c.closest_center_dist_miles !== undefined ? c.closest_center_dist_miles : ""}"`,
        `"${c.actual_center_dist_miles !== null && c.actual_center_dist_miles !== undefined ? c.actual_center_dist_miles : ""}"`,
        `"${c.exam_center_match || ""}"`
      ]);

      const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `june_2026_candidates_${dateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to export CSV: " + err);
    } finally {
      setExportingCSV(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#f0f2f4] min-h-screen">
      {/* Header Bar */}
      <header className="h-[66px] bg-white border-b border-[#e2e8f0] px-8 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div>
          <p className="text-[10.5px] tracking-widest uppercase text-[#666] font-bold">Aggregate Only</p>
          <h1 className="text-xl font-bold text-[#003d45] tracking-tight">Exam Cycle Oversight</h1>
        </div>

        {/* Cycle Switcher */}
        <div className="flex items-center gap-6">
          <div className="text-right">
            <span className="text-[10px] uppercase tracking-wider text-[#777] font-bold block">
              Exam Administration
            </span>
            <span className="text-xs font-bold text-[#007a8a]">{selectedCycle}</span>
          </div>

          <div className="flex bg-[#f0f2f4] p-1 rounded-lg border border-[#e2e8f0]">
            {(["June 2026", "December 2026"] as const).map((cycle) => (
              <button
                key={cycle}
                onClick={() => setSelectedCycle(cycle)}
                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${
                  selectedCycle === cycle
                    ? "bg-white text-[#003d45] shadow-sm"
                    : "text-[#666] hover:text-[#003d45]"
                }`}
              >
                <span>{cycle}</span>
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold uppercase tracking-tight ${
                    cycle === "June 2026"
                      ? "bg-slate-100 text-slate-600"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {cycle === "June 2026" ? "Snapshot" : "Live Feed"}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Sub-Nav Bar */}
      <nav className="bg-white border-b border-[#e2e8f0] px-8 flex gap-8">
        {[
          { id: "metrics", label: "Cycle Metrics" },
          { id: "coverage", label: "Center Coverage Analysis" },
          { id: "candidates", label: "Candidate Database" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id as any)}
            className={`py-3.5 text-xs font-bold border-b-2 transition-all ${
              activeSubTab === tab.id
                ? "border-[#007a8a] text-[#007a8a]"
                : "border-transparent text-[#666] hover:text-[#003d45]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Main Content Area */}
      <main className="p-8 max-w-[1600px] w-full mx-auto space-y-6">
        {loading && (
          <div className="p-12 text-center text-sm text-[#666] bg-white rounded-xl border border-[#e2e8f0]">
            Loading oversight data...
          </div>
        )}

        {error && (
          <div className="p-6 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl">
            <strong>Error:</strong> {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* SUB-TAB 1: CYCLE METRICS */}
            {activeSubTab === "metrics" && (
              <div className="space-y-6">
                {/* Aggregate Banner */}
                {selectedCycle === "December 2026" && data.last_updated && (
                  <div className="flex items-center gap-3 bg-[#e6f2f4] border border-[#b2d8dd] rounded-lg p-3 text-xs text-[#004d57] hover:bg-[#d8eff2] hover:border-[#007a8a]/50 hover:shadow-sm transition-all duration-300 cursor-pointer group">
                    <span className="material-symbols-outlined text-[18px] group-hover:scale-110 transition-transform duration-200">info</span>
                    <span>
                      Last synced: <strong>{formattedSyncTime}</strong>
                    </span>
                  </div>
                )}

                {/* Main Grid: Geography + Level Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Geography Bar Chart / List Card */}
                  <div className="lg:col-span-2 bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm hover:border-[#007a8a]/40 hover:shadow-md transition-all duration-300">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <div>
                        <h3 className="font-bold text-sm text-[#003d45] uppercase tracking-wider">
                          {showAllGeo ? "All" : "Top 10"} Countries & Communities — {selectedCycle}
                        </h3>
                        <p className="text-[11px] text-slate-500 font-medium">
                          {geoScaleType === "log" ? "Logarithmic Scale (log10) • " : "Linear Scale • "}
                          {showAllGeo
                            ? `Showing all ${data?.geo_breakdown?.length || 0} countries & ${data?.community_breakdown?.length || 0} communities`
                            : "Showing top 10 breakdown"}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {/* Top 10 vs All Toggle */}
                        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                          <button
                            onClick={() => setShowAllGeo(false)}
                            className={cn(
                              "px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              !showAllGeo ? "bg-white text-[#004d57] shadow-sm" : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            Top 10
                          </button>
                          <button
                            onClick={() => setShowAllGeo(true)}
                            className={cn(
                              "px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              showAllGeo ? "bg-white text-[#004d57] shadow-sm" : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            Show All
                          </button>
                        </div>

                        {/* Log Scale vs Linear Scale Toggle */}
                        {geoViewMode === "chart" && (
                          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                            <button
                              onClick={() => setGeoScaleType("linear")}
                              className={cn(
                                "px-2 py-1 text-[11px] font-semibold rounded-md transition-all",
                                geoScaleType === "linear" ? "bg-white text-[#004d57] shadow-sm" : "text-slate-500 hover:text-slate-800"
                              )}
                            >
                              Linear
                            </button>
                            <button
                              onClick={() => setGeoScaleType("log")}
                              className={cn(
                                "px-2 py-1 text-[11px] font-semibold rounded-md transition-all",
                                geoScaleType === "log" ? "bg-white text-[#004d57] shadow-sm" : "text-slate-500 hover:text-slate-800"
                              )}
                            >
                              Log Scale
                            </button>
                          </div>
                        )}

                        {/* List vs Chart View Mode */}
                        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                          <button
                            onClick={() => setGeoViewMode("list")}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              geoViewMode === "list"
                                ? "bg-white text-[#004d57] shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            <List className="w-3.5 h-3.5" />
                            <span>List</span>
                          </button>
                          <button
                            onClick={() => setGeoViewMode("chart")}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              geoViewMode === "chart"
                                ? "bg-white text-[#004d57] shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            <BarChart3 className="w-3.5 h-3.5" />
                            <span>Chart</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {geoViewMode === "list" ? (
                      /* Countries & Communities Grid List */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        {/* Countries */}
                        <div className="space-y-2">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-[#888] mb-3">
                            {showAllGeo ? `All Countries (${data.geo_breakdown.length})` : "Top 10 Countries"}
                          </p>
                          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1.5 custom-scrollbar">
                            {(showAllGeo ? data.geo_breakdown : data.geo_breakdown.slice(0, 10)).map((g) => (
                              <div
                                key={g.label}
                                className="flex items-center justify-between p-2.5 bg-[#f9fbfb] rounded-lg border border-[#e2e8f0] text-xs hover:border-[#007a8a]/50 hover:bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
                              >
                                <span className="font-medium text-[#222] group-hover:text-[#004D57] group-hover:font-bold truncate max-w-[120px] transition-colors">{g.label}</span>
                                <span className="font-bold text-[#007a8a] group-hover:scale-105 transition-transform">
                                  {g.value} <span className="text-[10px] text-[#888] font-normal">({g.pct}%)</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Communities */}
                        <div className="space-y-2">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-[#888] mb-3">
                            {showAllGeo ? `All Communities (${data.community_breakdown?.length || 0})` : "Top 10 Communities"}
                          </p>
                          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1.5 custom-scrollbar">
                            {data.community_breakdown && (showAllGeo ? data.community_breakdown : data.community_breakdown.slice(0, 10)).map((c) => (
                              <div
                                key={c.label}
                                className="flex items-center justify-between p-2.5 bg-[#f9fbfb] rounded-lg border border-[#e2e8f0] text-xs hover:border-[#007a8a]/50 hover:bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
                              >
                                <span className="font-medium text-[#222] group-hover:text-[#004D57] group-hover:font-bold truncate max-w-[120px] transition-colors">{c.label}</span>
                                <span className="font-bold text-[#007a8a] group-hover:scale-105 transition-transform">
                                  {c.value} <span className="text-[10px] text-[#888] font-normal">({c.pct}%)</span>
                                </span>
                              </div>
                            ))}
                            {(!data.community_breakdown || data.community_breakdown.length === 0) && (
                              <div className="p-3 text-center text-xs text-[#888] bg-[#f9fbfb] border border-dashed border-[#e2e8f0] rounded-lg">
                                No community data available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Countries & Communities Charts */
                      <div className="space-y-6 pt-2">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-[#004d57]">
                              {showAllGeo ? `All Countries (${data.geo_breakdown.length})` : "Top 10 Countries"}
                              {geoScaleType === "log" && <span className="ml-1 text-[10px] font-normal text-slate-500 uppercase">(Log Scale)</span>}
                            </p>
                          </div>
                          <div className="bg-[#f9fbfb] p-2 rounded-xl border border-[#e2e8f0] max-h-[500px] overflow-y-auto custom-scrollbar">
                            <ReactECharts
                              option={geoCountriesChartOption}
                              style={{ height: Math.max(260, (showAllGeo ? data.geo_breakdown.length : 10) * 26) }}
                              notMerge
                            />
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-[#0284c7]">
                              {showAllGeo ? `All Communities (${data.community_breakdown?.length || 0})` : "Top 10 Communities"}
                              {geoScaleType === "log" && <span className="ml-1 text-[10px] font-normal text-slate-500 uppercase">(Log Scale)</span>}
                            </p>
                          </div>
                          {data.community_breakdown && data.community_breakdown.length > 0 ? (
                            <div className="bg-[#f9fbfb] p-2 rounded-xl border border-[#e2e8f0] max-h-[500px] overflow-y-auto custom-scrollbar">
                              <ReactECharts
                                option={geoCommunitiesChartOption}
                                style={{ height: Math.max(260, (showAllGeo ? data.community_breakdown.length : 10) * 26) }}
                                notMerge
                              />
                            </div>
                          ) : (
                            <div className="p-4 text-center text-xs text-[#888] bg-[#f9fbfb] border border-dashed border-[#e2e8f0] rounded-lg">
                              No community chart data available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Registration & Scheduling Overview Table / Chart */}
                  <div className="lg:col-span-3 bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm hover:border-[#007a8a]/40 hover:shadow-md transition-all duration-300">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-sm text-[#003d45] uppercase tracking-wider">
                          Registration & Scheduling Overview
                        </h3>
                        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                          <button
                            onClick={() => setOverviewViewMode("table")}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              overviewViewMode === "table"
                                ? "bg-white text-[#004d57] shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            <Table className="w-3.5 h-3.5" />
                            <span>Table</span>
                          </button>
                          <button
                            onClick={() => setOverviewViewMode("chart")}
                            className={cn(
                              "flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-all",
                              overviewViewMode === "chart"
                                ? "bg-white text-[#004d57] shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                            )}
                          >
                            <BarChart3 className="w-3.5 h-3.5" />
                            <span>Chart</span>
                          </button>
                        </div>
                      </div>

                      {overviewViewMode === "table" ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl overflow-hidden">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-[11px] font-bold text-[#004d57] uppercase tracking-wider align-bottom">
                                  Level / Region
                                </th>
                                <th className="px-4 py-3 text-right text-[11px] font-bold text-[#004d57] uppercase tracking-wider">
                                  <div>Registered</div>
                                  <span className="text-[9px] font-medium text-slate-500 normal-case mt-0.5 block">
                                    % of Total Reg
                                  </span>
                                </th>
                                <th className="px-4 py-3 text-right text-[11px] font-bold text-[#004d57] uppercase tracking-wider">
                                  <div>Scheduled</div>
                                  <span className="text-[9px] font-medium text-teal-600 normal-case mt-0.5 block">
                                    % of Registered
                                  </span>
                                </th>
                                <th className="px-4 py-3 text-right text-[11px] font-bold text-[#004d57] uppercase tracking-wider">
                                  <div>Remote Sched</div>
                                  <span className="text-[9px] font-medium text-[#ef4621] normal-case mt-0.5 block">
                                    % of Scheduled
                                  </span>
                                </th>
                                <th className="px-4 py-3 text-right text-[11px] font-bold text-[#004d57] uppercase tracking-wider">
                                  <div>Cancelled</div>
                                  <span className="text-[9px] font-medium text-rose-600 normal-case mt-0.5 block">
                                    % of Registered
                                  </span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-[#e2e8f0]">
                              {/* SECTION: EXAM LEVELS */}
                              <tr className="bg-slate-50/60 border-y border-[#e2e8f0]">
                                <td colSpan={5} className="px-4 py-2.5 text-[10.5px] font-bold text-slate-600 uppercase tracking-wider pl-4">
                                  By Exam Level
                                </td>
                              </tr>
                              {/* Level 1 */}
                              <tr className="hover:bg-[#f9fbfb] transition-colors">
                                <td className="px-4 py-5 whitespace-nowrap text-sm font-bold text-[#003d45] pl-6">
                                  CMT Level 1
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-slate-800">{l1Reg}</span>
                                  <span className="text-xs text-slate-500 ml-1.5 font-medium">
                                    ({totalRegistered ? ((l1Reg / totalRegistered) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-teal-700">{l1Sched}</span>
                                  <span className="text-xs text-teal-600 ml-1.5 font-semibold">
                                    ({l1Reg ? ((l1Sched / l1Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-[#ef4621]">{l1Remote}</span>
                                  <span className="text-xs text-[#ef4621] ml-1.5 font-semibold">
                                    ({l1Sched ? ((l1Remote / l1Sched) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-rose-700">{l1Cancel}</span>
                                  <span className="text-xs text-rose-600 ml-1.5 font-semibold">
                                    ({l1Reg ? ((l1Cancel / l1Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                              </tr>

                              {/* Level 2 */}
                              <tr className="hover:bg-[#f9fbfb] transition-colors">
                                <td className="px-4 py-5 whitespace-nowrap text-sm font-bold text-[#003d45] pl-6">
                                  CMT Level 2
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-slate-800">{l2Reg}</span>
                                  <span className="text-xs text-slate-500 ml-1.5 font-medium">
                                    ({totalRegistered ? ((l2Reg / totalRegistered) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-teal-700">{l2Sched}</span>
                                  <span className="text-xs text-teal-600 ml-1.5 font-semibold">
                                    ({l2Reg ? ((l2Sched / l2Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-[#ef4621]">{l2Remote}</span>
                                  <span className="text-xs text-[#ef4621] ml-1.5 font-semibold">
                                    ({l2Sched ? ((l2Remote / l2Sched) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-rose-700">{l2Cancel}</span>
                                  <span className="text-xs text-rose-600 ml-1.5 font-semibold">
                                    ({l2Reg ? ((l2Cancel / l2Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                              </tr>

                              {/* Level 3 */}
                              <tr className="hover:bg-[#f9fbfb] transition-colors">
                                <td className="px-4 py-5 whitespace-nowrap text-sm font-bold text-[#003d45] pl-6">
                                  CMT Level 3
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-slate-800">{l3Reg}</span>
                                  <span className="text-xs text-slate-500 ml-1.5 font-medium">
                                    ({totalRegistered ? ((l3Reg / totalRegistered) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-teal-700">{l3Sched}</span>
                                  <span className="text-xs text-teal-600 ml-1.5 font-semibold">
                                    ({l3Reg ? ((l3Sched / l3Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-[#ef4621]">{l3Remote}</span>
                                  <span className="text-xs text-[#ef4621] ml-1.5 font-semibold">
                                    ({l3Sched ? ((l3Remote / l3Sched) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                                <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                  <span className="font-semibold text-rose-700">{l3Cancel}</span>
                                  <span className="text-xs text-rose-600 ml-1.5 font-semibold">
                                    ({l3Reg ? ((l3Cancel / l3Reg) * 100).toFixed(1) : 0}%)
                                  </span>
                                </td>
                              </tr>

                               {/* Level Total Row */}
                               <tr className="bg-slate-100/90 border-t-2 border-b-2 border-slate-300 font-bold">
                                 <td className="px-4 py-3.5 whitespace-nowrap text-sm font-black text-[#004d57] pl-6">
                                   Total
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-slate-900">
                                   {(l1Reg + l2Reg + l3Reg).toLocaleString()}
                                   <span className="text-xs text-slate-500 ml-1.5 font-bold">
                                     ({totalRegistered ? (((l1Reg + l2Reg + l3Reg) / totalRegistered) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#4b8892]">
                                   {(l1Sched + l2Sched + l3Sched).toLocaleString()}
                                   <span className="text-xs text-[#4b8892] ml-1.5 font-bold">
                                     ({(l1Reg + l2Reg + l3Reg) ? (((l1Sched + l2Sched + l3Sched) / (l1Reg + l2Reg + l3Reg)) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#ef4621]">
                                   {(l1Remote + l2Remote + l3Remote).toLocaleString()}
                                   <span className="text-xs text-[#ef4621] ml-1.5 font-bold">
                                     ({(l1Sched + l2Sched + l3Sched) ? (((l1Remote + l2Remote + l3Remote) / (l1Sched + l2Sched + l3Sched)) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#ae232d]">
                                   {(l1Cancel + l2Cancel + l3Cancel).toLocaleString()}
                                   <span className="text-xs text-[#ae232d] ml-1.5 font-bold">
                                     ({(l1Reg + l2Reg + l3Reg) ? (((l1Cancel + l2Cancel + l3Cancel) / (l1Reg + l2Reg + l3Reg)) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                               </tr>

                              {/* SECTION: REGIONS */}
                              <tr className="bg-slate-50/60 border-y border-[#e2e8f0]">
                                <td colSpan={5} className="px-4 py-2.5 text-[10.5px] font-bold text-slate-600 uppercase tracking-wider pl-4">
                                  By Region
                                </td>
                              </tr>
                              {data.region_scheduling?.map((r) => (
                                <tr key={r.region} className="hover:bg-[#f9fbfb] transition-colors">
                                  <td className="px-4 py-5 whitespace-nowrap text-sm font-bold text-[#003d45] pl-6">
                                    {r.region === "AMER" && "Americas (AMER)"}
                                    {r.region === "APAC" && "Asia-Pacific (APAC)"}
                                    {r.region === "EMEA" && "Europe, Middle East & Africa (EMEA)"}
                                    {r.region === "LATAM" && "Latin America (LATAM)"}
                                    {!["AMER", "APAC", "EMEA", "LATAM"].includes(r.region) && r.region}
                                  </td>
                                  <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                    <span className="font-semibold text-slate-800">{r.registered}</span>
                                    <span className="text-xs text-slate-500 ml-1.5 font-medium">
                                      ({totalRegistered ? ((r.registered / totalRegistered) * 100).toFixed(1) : 0}%)
                                    </span>
                                  </td>
                                  <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                    <span className="font-semibold text-teal-700">{r.scheduled}</span>
                                    <span className="text-xs text-teal-600 ml-1.5 font-semibold">
                                      ({r.registered ? ((r.scheduled / r.registered) * 100).toFixed(1) : 0}%)
                                    </span>
                                  </td>
                                  <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                    <span className="font-semibold text-[#ef4621]">{r.remote}</span>
                                    <span className="text-xs text-[#ef4621] ml-1.5 font-semibold">
                                      ({r.scheduled ? ((r.remote / r.scheduled) * 100).toFixed(1) : 0}%)
                                    </span>
                                  </td>
                                  <td className="px-4 py-5 whitespace-nowrap text-right text-sm">
                                    <span className="font-semibold text-rose-700">{r.cancelled || 0}</span>
                                    <span className="text-xs text-rose-600 ml-1.5 font-semibold">
                                      ({r.registered ? (((r.cancelled || 0) / r.registered) * 100).toFixed(1) : 0}%)
                                    </span>
                                  </td>
                                </tr>
                              ))}

                               {/* Region Total Row */}
                               <tr className="bg-slate-100/90 border-t-2 border-b-2 border-slate-300 font-bold">
                                 <td className="px-4 py-3.5 whitespace-nowrap text-sm font-black text-[#004d57] pl-6">
                                   Total
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-slate-900">
                                   {totalRegistered.toLocaleString()}
                                   <span className="text-xs text-slate-500 ml-1.5 font-bold">
                                     (100.0%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#4b8892]">
                                   {totalSched.toLocaleString()}
                                   <span className="text-xs text-[#4b8892] ml-1.5 font-bold">
                                     ({totalRegistered ? ((totalSched / totalRegistered) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#ef4621]">
                                   {totalRemote.toLocaleString()}
                                   <span className="text-xs text-[#ef4621] ml-1.5 font-bold">
                                     ({totalSched ? ((totalRemote / totalSched) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                                 <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-extrabold text-[#ae232d]">
                                   {totalCancel.toLocaleString()}
                                   <span className="text-xs text-[#ae232d] ml-1.5 font-bold">
                                     ({totalRegistered ? ((totalCancel / totalRegistered) * 100).toFixed(1) : 0}%)
                                   </span>
                                 </td>
                               </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="space-y-6 pt-2">
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="bg-[#f9fbfb] p-4 rounded-xl border border-[#e2e8f0]">
                              <ReactECharts option={overviewLevelChartOption} style={{ height: 340 }} notMerge />
                            </div>
                            <div className="bg-[#f9fbfb] p-4 rounded-xl border border-[#e2e8f0]">
                              <ReactECharts option={overviewRegionChartOption} style={{ height: 340 }} notMerge />
                            </div>
                          </div>

                          <div className="border-t border-[#e2e8f0] pt-6">
                            <h4 className="font-bold text-xs text-[#004d57] uppercase tracking-wider mb-4 text-center">
                              Scheduled Delivery Modality — In-Center (Physical) vs Remote (Virtual)
                            </h4>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                              <div className="bg-[#f9fbfb] p-4 rounded-xl border border-[#e2e8f0]">
                                <ReactECharts option={schedulingDeliveryLevelChartOption} style={{ height: 340 }} notMerge />
                              </div>
                              <div className="bg-[#f9fbfb] p-4 rounded-xl border border-[#e2e8f0]">
                                <ReactECharts option={schedulingDeliveryRegionChartOption} style={{ height: 340 }} notMerge />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Key Summary Cards Grid using official CMT Brand Standards palette */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                  {/* Total Registered */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#004d57] rounded-xl p-5 shadow-sm hover:border-[#004d57] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-bold text-[#004d57] group-hover:scale-105 transition-transform">{data.summary.registered}</p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#004d57] transition-colors">
                      Active Registered Candidates
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {data.summary.total_records} {selectedCycle === "June 2026" ? "Total Records in Snapshot" : "Active Registrations in CRM"}
                    </p>
                  </div>

                  {/* Freshly Paid Registrations */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#4b8892] rounded-xl p-5 shadow-sm hover:border-[#4b8892] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-bold text-[#004d57] group-hover:scale-105 transition-transform">
                      {(data.summary.freshly_paid ?? 0).toLocaleString()}
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#4b8892] transition-colors">
                      Freshly Paid Registrations
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      Total minus Deferred Registrations
                    </p>
                  </div>

                  {/* Total Scheduled */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#004d57] rounded-xl p-5 shadow-sm hover:border-[#004d57] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-extrabold text-[#004d57] group-hover:scale-105 transition-transform">
                      {data.scheduled_summary?.total_scheduled ? (
                        <>
                          {data.scheduled_summary.total_scheduled.toLocaleString()}{" "}
                          <span className="text-xs font-bold text-[#4b8892]">
                            ({data.scheduled_summary.scheduled_rate_pct}% Scheduled)
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-[#ef4621]">Pre-Registration</span>
                      )}
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#004d57] transition-colors">
                      Total Exams Scheduled
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {data.scheduled_summary?.total_scheduled ? (
                        `L1: ${data.scheduled_summary.level_1} / L2: ${data.scheduled_summary.level_2} / L3: ${data.scheduled_summary.level_3}`
                      ) : (
                        "No schedule data"
                      )}
                    </p>
                  </div>

                  {/* Remote Exams Scheduled */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#ef4621] rounded-xl p-5 shadow-sm hover:border-[#ef4621] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-extrabold text-[#004d57] group-hover:scale-105 transition-transform">
                      {data.scheduled_summary?.total_remote_scheduled !== undefined && data.scheduled_summary?.total_scheduled ? (
                        <>
                          {data.scheduled_summary.total_remote_scheduled.toLocaleString()}{" "}
                          <span className="text-xs font-bold text-[#ef4621]">
                            ({data.scheduled_summary.remote_pct_of_scheduled}% of Scheduled)
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-[#ef4621]">Pre-Registration</span>
                      )}
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#ef4621] transition-colors">
                      Remote Exams Scheduled
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {data.scheduled_summary?.total_remote_scheduled !== undefined && data.scheduled_summary?.total_scheduled ? (
                        `L1: ${data.scheduled_summary.remote_level_1} / L2: ${data.scheduled_summary.remote_level_2} / L3: ${data.scheduled_summary.remote_level_3}`
                      ) : (
                        "No schedule data"
                      )}
                    </p>
                  </div>

                  {/* Actual Test Takers & Sit Rate */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#4b8892] rounded-xl p-5 shadow-sm hover:border-[#4b8892] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-extrabold text-[#004d57] group-hover:scale-105 transition-transform">
                      {(data.tested_summary?.total_sat || 0) > 0 ? (
                        <>
                          {data.tested_summary?.total_sat.toLocaleString()}{" "}
                          <span className="text-xs font-bold text-[#4b8892]">
                            ({data.tested_summary?.sit_rate_pct}% Sit Rate)
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-[#ef4621]">Registration Phase</span>
                      )}
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#4b8892] transition-colors">
                      Actual Exam Test Takers
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {(data.tested_summary?.total_sat || 0) > 0
                        ? `${data.tested_summary?.physical_test_takers || 0} Physical / ${data.tested_summary?.virtual_test_takers || 0} Virtual`
                        : "Post-Exam Attendance Pending"}
                    </p>
                  </div>

                  {/* Exam Modality Split */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#5e4d66] rounded-xl p-5 shadow-sm hover:border-[#5e4d66] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-extrabold text-[#5e4d66] group-hover:scale-105 transition-transform">
                      {(data.tested_summary?.total_sat || 0) > 0 ? (
                        <>
                          {data.tested_summary?.physical_pct}%{" "}
                          <span className="text-xs font-semibold text-[#8a8a8a]">Physical</span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-[#5e4d66]">Pre-Exam Window</span>
                      )}
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#5e4d66] transition-colors">
                      Physical vs Remote Split
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {(data.tested_summary?.total_sat || 0) > 0
                        ? `${data.tested_summary?.virtual_pct}% Virtual Proctored`
                        : "Prometric Roster In-Progress"}
                    </p>
                  </div>

                  {/* Cancellation Rate */}
                  <div className="bg-white border border-[#e2e8f0] border-t-4 border-t-[#ae232d] rounded-xl p-5 shadow-sm hover:border-[#ae232d] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                    <p className="text-2xl font-bold text-[#ae232d] group-hover:scale-105 transition-transform">
                      {data.summary.cancellation_rate_pct}%
                    </p>
                    <p className="text-[10.5px] uppercase font-bold text-[#595959] mt-2 tracking-wider group-hover:text-[#ae232d] transition-colors">
                      Cancellation Rate
                    </p>
                    <p className="text-[11px] text-[#8a8a8a] mt-1">
                      {data.summary.cancelled} Cancelled Registrations
                    </p>
                  </div>
                </div>

                {/* Prometric Test Centers & Cities (Full Catalog with Search & Scroll) */}
                {data.top_test_centers && data.top_test_centers.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Prometric Test Centers Card */}
                    <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm hover:border-[#004d57]/40 hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <h3 className="font-bold text-sm text-[#004d57] uppercase tracking-wider">
                              Prometric Test Centers ({data.top_test_centers.length})
                            </h3>
                            <p className="text-[11px] text-slate-500 font-medium">Physical centers with candidate attendance</p>
                          </div>
                          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-teal-50 text-[#004d57] border border-[#004d57]/20 uppercase">
                            {data.top_test_centers.length} Active Sites
                          </span>
                        </div>

                        {/* Search Bar */}
                        <div className="relative mb-3">
                          <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                          <input
                            type="text"
                            value={centerSearch}
                            onChange={(e) => setCenterSearch(e.target.value)}
                            placeholder="Search by site name, city, country, or code..."
                            className="w-full text-xs pl-8 pr-7 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#004d57] focus:bg-white transition-all placeholder:text-slate-400"
                          />
                          {centerSearch && (
                            <button
                              onClick={() => setCenterSearch("")}
                              className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Scrollable Center List */}
                        <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto pr-1.5 custom-scrollbar">
                          {filteredCenters.map((center, idx) => (
                            <div
                              key={idx}
                              className="py-2.5 px-3 -mx-1 rounded-lg flex items-center justify-between text-xs hover:bg-[#f0f7f8] hover:border-l-4 hover:border-l-[#004d57] transition-all duration-200 cursor-pointer group"
                            >
                              <div className="pr-4 min-w-0">
                                <p className="font-bold text-slate-800 truncate group-hover:text-[#004d57] transition-colors">
                                  {center.site_name}
                                </p>
                                <p className="text-[11px] text-slate-500">
                                  {center.city}, {center.country} &bull;{" "}
                                  <span className="font-mono text-slate-400 group-hover:text-[#004d57] transition-colors">
                                    Code {center.center_code}
                                  </span>
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-extrabold text-[#004d57] text-sm group-hover:scale-110 block transition-transform">
                                  {center.count} {center.count === 1 ? "candidate" : "candidates"}
                                </span>
                                <p className="text-[10px] text-slate-400 font-semibold">{center.pct_of_physical}% of physical</p>
                              </div>
                            </div>
                          ))}

                          {filteredCenters.length === 0 && (
                            <div className="py-8 text-center text-xs text-slate-500 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                              No test centers match &ldquo;{centerSearch}&rdquo;
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expand / Collapse Footer */}
                      {!centerSearch && data.top_test_centers.length > 10 && (
                        <div className="pt-3 mt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] text-slate-500 font-medium">
                            {showAllCenters
                              ? `Showing all ${data.top_test_centers.length} centers (scrollable)`
                              : `Showing top 10 of ${data.top_test_centers.length} centers`}
                          </span>
                          <button
                            onClick={() => setShowAllCenters(!showAllCenters)}
                            className="text-xs font-bold text-[#004d57] hover:text-[#003d45] hover:underline transition-all flex items-center gap-1"
                          >
                            {showAllCenters ? "Show Top 10 Only" : `Show All ${data.top_test_centers.length} Centers →`}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Testing Cities Card */}
                    <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm hover:border-[#4b8892]/40 hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <h3 className="font-bold text-sm text-[#004d57] uppercase tracking-wider">
                              Candidate Testing Cities ({data.top_test_cities?.length || 0})
                            </h3>
                            <p className="text-[11px] text-slate-500 font-medium">Global candidate exam volume by city</p>
                          </div>
                          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-teal-50 text-[#4b8892] border border-[#4b8892]/20 uppercase">
                            {data.top_test_cities?.length || 0} Active Cities
                          </span>
                        </div>

                        {/* Search Bar */}
                        <div className="relative mb-3">
                          <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                          <input
                            type="text"
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            placeholder="Search by city or country..."
                            className="w-full text-xs pl-8 pr-7 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#4b8892] focus:bg-white transition-all placeholder:text-slate-400"
                          />
                          {citySearch && (
                            <button
                              onClick={() => setCitySearch("")}
                              className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Scrollable City List */}
                        <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto pr-1.5 custom-scrollbar">
                          {filteredCities.map((city, idx) => (
                            <div
                              key={idx}
                              className="py-2.5 px-3 -mx-1 rounded-lg flex items-center justify-between text-xs hover:bg-[#f0f7f8] hover:border-l-4 hover:border-l-[#4b8892] transition-all duration-200 cursor-pointer group"
                            >
                              <div className="flex items-center gap-2 pr-4 min-w-0">
                                <MapPin className="w-3.5 h-3.5 text-[#4b8892] shrink-0 group-hover:scale-125 group-hover:text-[#004d57] transition-transform duration-200" />
                                <div className="truncate">
                                  <p className="font-bold text-slate-800 group-hover:text-[#004d57] transition-colors truncate">
                                    {city.city}
                                  </p>
                                  <p className="text-[11px] text-slate-500 truncate">{city.country}</p>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-extrabold text-[#4b8892] text-sm group-hover:scale-110 block transition-transform">
                                  {city.count} {city.count === 1 ? "candidate" : "candidates"}
                                </span>
                                <p className="text-[10px] text-slate-400 font-semibold">{city.pct_of_physical}% of physical</p>
                              </div>
                            </div>
                          ))}

                          {filteredCities.length === 0 && (
                            <div className="py-8 text-center text-xs text-slate-500 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                              No testing cities match &ldquo;{citySearch}&rdquo;
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expand / Collapse Footer */}
                      {!citySearch && (data.top_test_cities?.length || 0) > 10 && (
                        <div className="pt-3 mt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] text-slate-500 font-medium">
                            {showAllCities
                              ? `Showing all ${data.top_test_cities?.length || 0} cities (scrollable)`
                              : `Showing top 10 of ${data.top_test_cities?.length || 0} cities`}
                          </span>
                          <button
                            onClick={() => setShowAllCities(!showAllCities)}
                            className="text-xs font-bold text-[#4b8892] hover:text-[#004d57] hover:underline transition-all flex items-center gap-1"
                          >
                            {showAllCities ? "Show Top 10 Only" : `Show All ${data.top_test_cities?.length || 0} Cities →`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SUB-TAB 2: CENTER COVERAGE ANALYSIS */}
            {activeSubTab === "coverage" && (
              <div className="space-y-6">
                {/* Metric Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="bg-white p-5 border border-[#e2e8f0] rounded-xl shadow-sm">
                    <p className="text-xs uppercase font-bold text-[#666] tracking-wider">Total Active Centers</p>
                    <p className="text-3xl font-extrabold text-[#003d45] mt-1">{data.coverage.test_centers_total}</p>
                    <p className="text-[11px] text-[#888] mt-1">Prometric test centers</p>
                  </div>

                  <div className="bg-white p-5 border border-[#e2e8f0] rounded-xl shadow-sm">
                    <p className="text-xs uppercase font-bold text-[#666] tracking-wider">Direct Access (&le;100km)</p>
                    <p className="text-3xl font-extrabold text-emerald-600 mt-1">
                      {data.coverage.candidates_direct_access}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                      {data.coverage.pct_direct_access}% of registered
                    </p>
                  </div>

                  <div className="bg-white p-5 border border-[#e2e8f0] rounded-xl shadow-sm">
                    <p className="text-xs uppercase font-bold text-[#666] tracking-wider">Moderate Gap (&gt;100km)</p>
                    <p className="text-3xl font-extrabold text-amber-600 mt-1">
                      {data.coverage.candidates_gap_100km}
                    </p>
                    <p className="text-[11px] text-amber-700 font-semibold mt-1">
                      {data.coverage.pct_gap}% of registered
                    </p>
                  </div>

                  <div className="bg-white p-5 border border-[#e2e8f0] rounded-xl shadow-sm">
                    <p className="text-xs uppercase font-bold text-[#666] tracking-wider">Severe Gap (&gt;200km)</p>
                    <p className="text-3xl font-extrabold text-rose-600 mt-1">
                      {data.coverage.candidates_severe_gap_200km}
                    </p>
                    <p className="text-[11px] text-rose-700 font-semibold mt-1">
                      {data.coverage.pct_severe}% of registered
                    </p>
                  </div>
                </div>

                {/* Interactive Leaflet Prometric Coverage & Gap Map */}
                <PrometricCoverageMap cycle={selectedCycle} />

                {/* Country Breakdown Table */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-sm overflow-hidden p-6 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-sm text-[#003d45] uppercase tracking-wider">
                        Prometric Coverage & Gap Matrix by Country
                      </h3>
                      <p className="text-xs text-[#666]">
                        Aggregated candidate counts paired with Prometric site list metadata.
                      </p>
                    </div>

                    {/* Filter controls */}
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Search country..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="px-3 py-1.5 text-xs border border-[#ccc] rounded-lg focus:outline-none focus:border-[#007a8a]"
                      />
                      <select
                        value={coverageRegionFilter}
                        onChange={(e) => setCoverageRegionFilter(e.target.value)}
                        className="px-3 py-1.5 text-xs border border-[#ccc] rounded-lg focus:outline-none focus:border-[#007a8a]"
                      >
                        <option value="all">All Macro Regions</option>
                        <option value="AMER">Americas (AMER)</option>
                        <option value="APAC">Asia-Pacific (APAC)</option>
                        <option value="EMEA">Europe, Middle East & Africa (EMEA)</option>
                        <option value="LATAM">Latin America (LATAM)</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead>
                        <tr className="bg-[#f0f2f4] text-[#555] font-bold uppercase tracking-wider border-b border-[#e2e8f0]">
                          <th className="py-3 px-4">Country</th>
                          <th className="py-3 px-4">Region</th>
                          <th className="py-3 px-4 text-right">Candidates</th>
                          <th className="py-3 px-4 text-center">L1 / L2 / L3</th>
                          <th className="py-3 px-4 text-right">Prometric Centers</th>
                          <th className="py-3 px-4 text-right">Workstations</th>
                          <th className="py-3 px-4 text-right">Ratio (Cand:Center)</th>
                          <th className="py-3 px-4 text-center">Coverage Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#e2e8f0]">
                        {filteredCountries.map((row) => (
                          <tr key={row.country} className="hover:bg-[#f9fbfb]">
                            <td className="py-3 px-4 font-bold text-[#111]">{row.country}</td>
                            <td className="py-3 px-4 text-[#666]">{row.macro_region}</td>
                            <td className="py-3 px-4 text-right font-extrabold text-[#007a8a]">
                              {row.candidates}
                            </td>
                            <td className="py-3 px-4 text-center text-[#555]">
                              {row.level1} / {row.level2} / {row.level3}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold">
                              {row.prometric_centers}
                            </td>
                            <td className="py-3 px-4 text-right text-[#666]">
                              {row.workstations}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold">
                              {row.ratio ? `${row.ratio}:1` : "N/A"}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span
                                className={`px-2.5 py-1 text-[10.5px] font-bold rounded-full uppercase ${
                                  row.coverage === "Healthy"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : row.coverage === "Moderate"
                                    ? "bg-amber-100 text-amber-800"
                                    : row.coverage === "Strained"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-rose-100 text-rose-800"
                                }`}
                              >
                                {row.coverage}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-TAB 3: CANDIDATE DATABASE (Staff Directory Style with In-Header Filters) */}
            {activeSubTab === "candidates" && (
              <motion.section
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                {/* Header Section */}
                <div className="flex flex-col gap-6 px-2">
                  <div className="space-y-1">
                    <h2 className="text-3xl font-normal text-[#004D57] uppercase tracking-tighter flex items-center gap-4">
                      <GraduationCap className="w-8 h-8 text-[#AE232D]" />
                      LIVE CANDIDATE RECORDS — {selectedCycle}
                    </h2>
                    <p className="text-slate-500 text-[10px] font-normal uppercase tracking-widest">
                      Internal Candidate Database View: Direct access to candidate details, registration statuses, exam levels, and geographical assignments.
                    </p>
                  </div>

                  {/* Clean Top Toolbar (Search + Export + Clear) */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-3 w-full justify-start">
                    {/* Global Keyword Search */}
                    <div className="relative group flex-1 w-full min-w-[320px]">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-[#004D57] transition-colors" />
                      <Input
                        placeholder="GLOBAL CANDIDATE SEARCH (NAME, ID, COUNTRY, LEVEL, REGION, COMMUNITY, CHAPTER)..."
                        className="pl-12 h-14 bg-white border-slate-100 rounded-2xl shadow-xl focus:ring-2 focus:ring-[#004D57] font-normal text-xs tracking-widest uppercase"
                        value={candidateSearch}
                        onChange={(e) => {
                          setCandidateSearch(e.target.value);
                          setCandidatePage(1);
                        }}
                      />
                    </div>

                    {/* Export CSV Button */}
                    <Button
                      onClick={exportCandidatesCSV}
                      disabled={exportingCSV || candidatesLoading || totalCandidateRecords === 0}
                      className="h-14 px-6 bg-[#004D57] hover:bg-[#004D57]/90 text-white font-normal text-[10px] uppercase tracking-widest rounded-2xl shrink-0 flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all disabled:opacity-55"
                    >
                      {exportingCSV ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {exportingCSV ? "Exporting..." : "Export CSV"}
                    </Button>

                    {/* Clear Filters Button */}
                    {hasCandidateFilters && (
                      <Button
                        onClick={clearCandidateFilters}
                        variant="ghost"
                        className="h-10 px-4 border border-dashed border-[#AE232D]/30 text-[#AE232D] font-bold text-[9px] uppercase tracking-widest hover:bg-[#AE232D]/5 rounded-xl transition-all shrink-0 flex items-center gap-1.5"
                      >
                        <X className="w-3.5 h-3.5" /> Clear Filters
                      </Button>
                    )}
                  </div>
                </div>

                {/* Table Container Card */}
                <div className="bg-white rounded-[3rem] border border-slate-50 shadow-2xl overflow-hidden p-2 relative min-h-[600px] flex flex-col">
                  {candidatesLoading && (
                    <div className="absolute top-2 left-2 right-2 h-0.5 z-30 overflow-hidden rounded-full">
                      <div className="h-full bg-[#AE232D] rounded-full" style={{ animation: 'progress-slide 1.2s ease-in-out infinite' }} />
                    </div>
                  )}

                  {/* Top Pagination & Info Header */}
                  <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-t-3xl">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
                        {totalCandidateRecords.toLocaleString()} Total Candidates &nbsp;·&nbsp; Page {candidatePage} of {totalCandidatePages}
                      </span>
                      <select
                        value={candidatePageSize}
                        onChange={(e) => {
                          setCandidatePageSize(Number(e.target.value));
                          setCandidatePage(1);
                        }}
                        className="text-[9px] uppercase font-bold text-[#004D57] bg-white border border-slate-200 rounded-lg px-2 py-1 outline-none"
                      >
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={250}>250 per page</option>
                        <option value={2000}>ALL ({totalCandidateRecords})</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setCandidatePage((p) => Math.max(1, p - 1))}
                        disabled={candidatePage <= 1 || candidatesLoading}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-3 h-3" /> Prev
                      </button>
                      <span className="text-[10px] font-normal text-slate-400 tabular-nums">
                        {candidatePage} / {totalCandidatePages}
                      </span>
                      <button
                        onClick={() => setCandidatePage((p) => Math.min(totalCandidatePages, p + 1))}
                        disabled={candidatePage >= totalCandidatePages || candidatesLoading}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Next <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {/* Dual Scroll Table Wrapper */}
                  <DualScrollWrapper>
                    <div className="rounded-2xl border border-slate-50 relative min-w-max">
                      <table className="w-full border-collapse text-left">
                        <thead className="sticky top-0 z-20 shadow-sm">
                          <tr className="bg-[#004D57] text-white">
                            {/* Member ID */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[90px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="member_id" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="relative group/search">
                                  <Input
                                    type="text"
                                    placeholder="ID..."
                                    value={candidateIdSearch}
                                    onChange={(e) => {
                                      setCandidateIdSearch(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[75px]"
                                  />
                                </div>
                              </div>
                            </th>
                            {/* Name */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[140px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="name" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="relative group/search">
                                  <Input
                                    type="text"
                                    placeholder="NAME..."
                                    value={candidateNameSearch}
                                    onChange={(e) => {
                                      setCandidateNameSearch(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[130px]"
                                  />
                                  <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                                </div>
                              </div>
                            </th>

                            {/* Email Address */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[150px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="email" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="relative group/search">
                                  <Input
                                    type="text"
                                    placeholder="EMAIL..."
                                    value={filterEmail}
                                    onChange={(e) => {
                                      setFilterEmail(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[140px]"
                                  />
                                  <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                                </div>
                              </div>
                            </th>

                            {/* Phone Number */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="phone" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="relative group/search">
                                  <Input
                                    type="text"
                                    placeholder="PHONE..."
                                    value={filterPhone}
                                    onChange={(e) => {
                                      setFilterPhone(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[110px]"
                                  />
                                  <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                                </div>
                              </div>
                            </th>

                            {/* Discord ID */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="discord_id" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="relative group/search">
                                  <Input
                                    type="text"
                                    placeholder="DISCORD..."
                                    value={filterDiscord}
                                    onChange={(e) => {
                                      setFilterDiscord(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[110px]"
                                  />
                                  <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                                </div>
                              </div>
                            </th>

                            {/* Exam Level */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[95px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="level" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="LEVEL"
                                  plural="LEVELS"
                                  options={["Level 1", "Level 2", "Level 3"]}
                                  selected={filterLevel}
                                  isExclude={excludeLevel}
                                  onExcludeToggle={setExcludeLevel}
                                  onChange={(vals) => {
                                    setFilterLevel(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Registration Status */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[95px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="status" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="STATUS"
                                  plural="STATUSES"
                                  options={["registered", "deferred", "cancelled"]}
                                  selected={filterStatus}
                                  isExclude={excludeStatus}
                                  onExcludeToggle={setExcludeStatus}
                                  onChange={(vals) => {
                                    setFilterStatus(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Exam Attendance */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="has_sat_exam" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="ATTENDANCE"
                                  plural="ATTENDANCES"
                                  options={["Sat Exam", "Pending / Scheduled"]}
                                  selected={filterAttendance}
                                  isExclude={excludeAttendance}
                                  onExcludeToggle={setExcludeAttendance}
                                  onChange={(vals) => {
                                    setFilterAttendance(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Prometric Center */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[170px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="actual_site_name" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="PROMETRIC CENTER"
                                  plural="PROMETRIC CENTERS"
                                  options={availablePrometricCenters}
                                  selected={filterPrometricCenter}
                                  isExclude={excludePrometricCenter}
                                  onExcludeToggle={setExcludePrometricCenter}
                                  onChange={(vals) => {
                                    setFilterPrometricCenter(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Distance to Closest Center (Miles) */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap w-[115px] min-w-[115px] max-w-[115px] overflow-hidden">
                              <div className="flex flex-col gap-1.5">
                                <SortToggle col="closest_center_dist_miles" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    placeholder="MIN"
                                    value={filterDistMin}
                                    onChange={(e) => {
                                      setFilterDistMin(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-5 bg-white/5 border-white/10 text-[8px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all px-1 rounded font-normal w-1/2"
                                  />
                                  <Input
                                    type="number"
                                    placeholder="MAX"
                                    value={filterDistMax}
                                    onChange={(e) => {
                                      setFilterDistMax(e.target.value);
                                      setCandidatePage(1);
                                    }}
                                    className="h-5 bg-white/5 border-white/10 text-[8px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all px-1 rounded font-normal w-1/2"
                                  />
                                </div>
                              </div>
                            </th>

                            {/* Exam Location Match */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap w-[145px] min-w-[145px] max-w-[145px] overflow-hidden">
                              <div className="flex flex-col gap-1.5">
                                <SortToggle col="exam_center_match" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="EXAM MATCH"
                                  plural="MATCHES"
                                  options={["Closest Center", "Other Center", "Virtual / Remote", "Registered / Pending"]}
                                  selected={filterCenterMatch}
                                  isExclude={excludeCenterMatch}
                                  onExcludeToggle={setExcludeCenterMatch}
                                  onChange={(vals) => {
                                    setFilterCenterMatch(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Membership Journey */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[140px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="membership_journey" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="MEMBERSHIP"
                                  plural="MEMBERSHIPS"
                                  options={availableMembershipJourneys}
                                  selected={filterMembershipJourney}
                                  isExclude={excludeMembershipJourney}
                                  onExcludeToggle={setExcludeMembershipJourney}
                                  onChange={(vals) => {
                                    setFilterMembershipJourney(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Payment Status */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[100px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="payment_status" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="PAYMENT"
                                  plural="PAYMENTS"
                                  options={["paid", "no-cost-order"]}
                                  selected={filterPayment}
                                  isExclude={excludePayment}
                                  onExcludeToggle={setExcludePayment}
                                  onChange={(vals) => {
                                    setFilterPayment(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Community (cluster_center_name) */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="community" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="COMMUNITY"
                                  plural="COMMUNITIES"
                                  options={availableCommunities}
                                  selected={filterCommunity}
                                  isExclude={excludeCommunity}
                                  onExcludeToggle={setExcludeCommunity}
                                  onChange={(vals) => {
                                    setFilterCommunity(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Chapter */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[110px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="chapter" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="CHAPTER"
                                  plural="CHAPTERS"
                                  options={availableChapters}
                                  selected={filterChapter}
                                  isExclude={excludeChapter}
                                  onExcludeToggle={setExcludeChapter}
                                  onChange={(vals) => {
                                    setFilterChapter(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* City / Locality (city_name) */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[130px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="city" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="CITY / LOCALITY"
                                  plural="CITIES / LOCALITIES"
                                  options={availableCities}
                                  selected={filterCity}
                                  isExclude={excludeCity}
                                  onExcludeToggle={setExcludeCity}
                                  onChange={(vals) => {
                                    setFilterCity(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* State (city_admin1) */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[110px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="state" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="STATE"
                                  plural="STATES"
                                  options={availableStates}
                                  selected={filterState}
                                  isExclude={excludeState}
                                  onExcludeToggle={setExcludeState}
                                  onChange={(vals) => {
                                    setFilterState(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Country (city_country) */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[130px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="country" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="COUNTRY"
                                  plural="COUNTRIES"
                                  options={availableCountries}
                                  selected={filterCountry}
                                  isExclude={excludeCountry}
                                  onExcludeToggle={setExcludeCountry}
                                  onChange={(vals) => {
                                    setFilterCountry(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>

                            {/* Macro Region */}
                            <th className="p-1.5 px-2 font-normal uppercase text-[9px] tracking-[0.2em] whitespace-nowrap min-w-[95px]">
                              <div className="flex flex-col gap-2">
                                <SortToggle col="region" sortConfig={candidateSortConfig} setSortConfig={setCandidateSortConfig} />
                                <PremiumMultiSelect
                                  label="REGION"
                                  plural="REGIONS"
                                  options={["AMER", "APAC", "EMEA", "LATAM"]}
                                  selected={filterRegion}
                                  isExclude={excludeRegion}
                                  onExcludeToggle={setExcludeRegion}
                                  onChange={(vals) => {
                                    setFilterRegion(vals);
                                    setCandidatePage(1);
                                  }}
                                />
                              </div>
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100 text-[10px]">
                          {candidatesLoading ? (
                            <tr>
                              <td colSpan={18} className="p-16 text-center text-slate-400 font-normal uppercase tracking-widest">
                                Loading candidate records...
                              </td>
                            </tr>
                          ) : paginatedCandidates.length === 0 ? (
                            <tr>
                              <td colSpan={18} className="p-16 text-center text-slate-400 font-normal uppercase tracking-widest">
                                No matching candidate records found.
                              </td>
                            </tr>
                          ) : (
                            paginatedCandidates.map((c, idx) => (
                              <tr key={c.member_id || idx} className="hover:bg-slate-50/80 transition-colors group">
                                {/* Member ID */}
                                <td className="p-3 px-3 border-r border-slate-50 font-mono text-[#004D57] font-bold whitespace-nowrap">
                                  <div className="flex items-center justify-between gap-1">
                                    <span>{c.member_id}</span>
                                    <button
                                      onClick={() => handleCopy(String(c.member_id), String(c.member_id))}
                                      title="Copy Member ID"
                                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200/50 rounded transition-all text-slate-400 hover:text-[#004D57]"
                                    >
                                      {copiedId === String(c.member_id) ? (
                                        <Check className="w-3 h-3 text-emerald-600" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                </td>

                                {/* Name */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  <div className="font-bold text-slate-900 text-xs">
                                    {c.first_name} {c.last_name}
                                  </div>
                                  {c.program_journey && (
                                    <span className="text-[9px] text-slate-400 font-medium">
                                      {c.program_journey}
                                    </span>
                                  )}
                                </td>

                                {/* Email Address */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap font-mono text-slate-700">
                                  {c.email ? (
                                    <div className="flex items-center justify-between gap-1">
                                      <div className="flex items-center gap-1">
                                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                                        <a href={`mailto:${c.email}`} className="hover:underline text-[#004D57]">
                                          {c.email}
                                        </a>
                                      </div>
                                      <button
                                        onClick={() => handleCopy(c.email, `email-${c.member_id}`)}
                                        title="Copy Email"
                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200/50 rounded transition-all text-slate-400 hover:text-[#004D57]"
                                      >
                                        {copiedId === `email-${c.member_id}` ? (
                                          <Check className="w-3 h-3 text-emerald-600" />
                                        ) : (
                                          <Copy className="w-3 h-3" />
                                        )}
                                      </button>
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                {/* Phone Number */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap text-slate-700">
                                  {c.phone ? (
                                    <div className="flex items-center gap-1">
                                      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                                      <span>{c.phone}</span>
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                {/* Discord ID */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap font-mono text-slate-600">
                                  {c.discord_id ? (
                                    <div className="flex items-center gap-1">
                                      <MessageSquare className="w-3 h-3 text-indigo-400 shrink-0" />
                                      <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-[9px]">
                                        {c.discord_id}
                                      </span>
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                {/* Level */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  <span className="px-2.5 py-1 text-[9px] font-bold uppercase rounded-lg bg-[#004D57]/10 text-[#004D57]">
                                    {c.level || "Level ?"}
                                  </span>
                                </td>

                                {/* Status */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  <span
                                    className={`px-2.5 py-1 text-[9px] font-bold rounded-lg uppercase ${
                                      c.status === "registered"
                                        ? "bg-emerald-100 text-emerald-800"
                                        : c.status === "deferred"
                                        ? "bg-amber-100 text-amber-800"
                                        : "bg-rose-100 text-rose-800"
                                    }`}
                                  >
                                    {c.status}
                                  </span>
                                </td>

                                {/* Exam Attendance (Sat Exam / Pending) */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  {c.has_sat_exam ? (
                                    <div>
                                      <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase rounded bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                                        <Check className="w-2.5 h-2.5" /> Sat Exam
                                      </span>
                                      {c.event_date && (
                                        <p className="text-[9.5px] text-slate-500 font-mono mt-0.5">{c.event_date}</p>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="px-2 py-0.5 text-[9px] font-semibold uppercase rounded bg-slate-100 text-slate-500">
                                      Pending / Scheduled
                                    </span>
                                  )}
                                </td>

                                {/* Joined Prometric Center */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  {c.is_virtual ? (
                                    <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-orange-50 text-[#ef4621] border border-[#ef4621]/30">
                                      Virtual / Remote Proctored
                                    </span>
                                  ) : c.actual_site_name ? (
                                    <div>
                                      <p className="font-bold text-slate-800 text-[11px] truncate max-w-[200px]">{c.actual_site_name}</p>
                                      <p className="text-[9.5px] text-slate-500">
                                        {c.actual_center_city}, {c.actual_center_country} {c.actual_center_code ? `(Code ${c.actual_center_code})` : ""}
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Distance to Closest Center (Miles) */}
                                <td className="p-2.5 px-2 border-r border-slate-50 whitespace-nowrap w-[115px] min-w-[115px] max-w-[115px] overflow-hidden">
                                  {c.closest_center_dist_miles !== null && c.closest_center_dist_miles !== undefined ? (
                                    <div>
                                      <span className="font-mono font-bold text-[#004D57] text-xs">
                                        {c.closest_center_dist_miles} mi
                                      </span>
                                      {c.closest_center_name && (
                                        <p className="text-[8.5px] text-slate-400 truncate max-w-[105px]" title={`${c.closest_center_name} (${c.closest_center_code})`}>
                                          {c.closest_center_name}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>

                                {/* Exam Location Match */}
                                <td className="p-2.5 px-2 border-r border-slate-50 whitespace-nowrap w-[145px] min-w-[145px] max-w-[145px] overflow-hidden">
                                  {c.exam_center_match === "closest_center" ? (
                                    <div>
                                      <span className="px-1.5 py-0.5 text-[8.5px] font-bold uppercase rounded bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                                        <Check className="w-2.5 h-2.5" /> Closest Center
                                      </span>
                                      <p className="text-[8.5px] text-emerald-700 font-mono mt-0.5 truncate max-w-[135px]">
                                        Sat at site ({c.closest_center_dist_miles} mi)
                                      </p>
                                    </div>
                                  ) : c.exam_center_match === "other_center" ? (
                                    <div>
                                      <span className="px-1.5 py-0.5 text-[8.5px] font-bold uppercase rounded bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
                                        Other Center
                                      </span>
                                      <p className="text-[8.5px] text-amber-700 font-mono mt-0.5 truncate max-w-[135px]">
                                        {c.actual_center_dist_miles !== null && c.actual_center_dist_miles !== undefined ? `${c.actual_center_dist_miles} mi act` : "Other site"} (vs {c.closest_center_dist_miles ?? "—"} mi)
                                      </p>
                                    </div>
                                  ) : c.exam_center_match === "virtual" ? (
                                    <span className="px-1.5 py-0.5 text-[8.5px] font-bold uppercase rounded bg-orange-50 text-[#ef4621] border border-[#ef4621]/30">
                                      Virtual / Remote
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 text-[8.5px] font-medium uppercase rounded bg-slate-100 text-slate-500">
                                      Registered / Pending
                                    </span>
                                  )}
                                </td>

                                {/* Membership Journey */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  {c.membership_journey ? (
                                    <span className="px-2 py-0.5 text-[9px] font-semibold rounded bg-teal-50 text-[#004d57] border border-[#004d57]/20">
                                      {c.membership_journey}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>

                                {/* Payment */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  <div className="flex flex-col">
                                    <span className="font-semibold text-slate-700 capitalize">
                                      {c.payment_status || "—"}
                                    </span>
                                    {c.payment_amount && (
                                      <span className="text-[9px] font-mono text-slate-400">
                                        ${c.payment_amount}
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Community (cluster_center_name) */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap font-medium text-slate-700">
                                  {c.community || "—"}
                                </td>

                                {/* Chapter */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap font-medium text-slate-700">
                                  {c.chapter || "—"}
                                </td>

                                {/* City (city_name) */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap font-medium text-slate-700">
                                  {c.city || "—"}
                                </td>

                                {/* State (city_admin1) */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap text-slate-600">
                                  {c.state || "—"}
                                </td>

                                {/* Country (city_country) */}
                                <td className="p-3 px-3 border-r border-slate-50 whitespace-nowrap">
                                  <div className="flex items-center gap-1.5">
                                    <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                                    <span className="font-semibold text-slate-800">{c.country || "—"}</span>
                                  </div>
                                </td>

                                {/* Region */}
                                <td className="p-3 px-3 whitespace-nowrap">
                                  <span className="px-2 py-0.5 text-[9px] font-bold rounded bg-slate-100 text-slate-700">
                                    {c.region || "—"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </DualScrollWrapper>

                  {/* Bottom Pagination Footer */}
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-b-3xl mt-2">
                    <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
                      Showing {paginatedCandidates.length > 0 ? (candidatePage - 1) * candidatePageSize + 1 : 0} to {Math.min(candidatePage * candidatePageSize, totalCandidateRecords)} of {totalCandidateRecords.toLocaleString()} candidate records
                    </span>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setCandidatePage((p) => Math.max(1, p - 1))}
                        disabled={candidatePage <= 1 || candidatesLoading}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-3 h-3" /> Prev
                      </button>
                      <span className="text-[10px] font-normal text-slate-400 tabular-nums">
                        {candidatePage} / {totalCandidatePages}
                      </span>
                      <button
                        onClick={() => setCandidatePage((p) => Math.min(totalCandidatePages, p + 1))}
                        disabled={candidatePage >= totalCandidatePages || candidatesLoading}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-[#004D57]/30 disabled:cursor-not-allowed"
                      >
                        Next <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
